"use client";

import { LazyMotion, domAnimation, m, useReducedMotion } from "framer-motion";

import type { HomeDictionary } from "@/content/home";

interface GlobalOperationsProps {
  copy: HomeDictionary["operations"];
}

export default function GlobalOperations({ copy }: GlobalOperationsProps) {
  const shouldReduceMotion = useReducedMotion();
  const viewport = { once: true, amount: 0.25 };

  return (
    <LazyMotion features={domAnimation}>
      <section id="operaciones" className="bg-white py-24 lg:py-32 border-t border-gray-100">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <m.div
            initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 34 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={viewport}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="flex flex-col lg:flex-row justify-between items-start lg:items-end mb-24 gap-8"
          >
          <div className="max-w-2xl">
            <span className="text-terracotta text-[10px] font-bold tracking-[0.4em] uppercase mb-4 block">
              {copy.eyebrow}
            </span>
            <h2 className="text-petroleum text-5xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.85]">
              {copy.title} <br />
              <span className="text-slate-300">{copy.accent}</span>
            </h2>
          </div>
          <div className="max-w-xs">
            <p className="text-slate-600 text-sm leading-relaxed border-l border-terracotta pl-6">
              {copy.description}
            </p>
          </div>
          </m.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-100 border border-slate-100">
            {copy.regions.map((region, index) => (
              <m.div
              key={`${region.type}-${region.name}`}
              initial={shouldReduceMotion ? { opacity: 1 } : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewport}
              transition={{ duration: 0.55, delay: shouldReduceMotion ? 0 : index * 0.08, ease: "easeOut" }}
              className="group relative bg-white p-10 lg:p-12 transition-all duration-500 hover:z-10"
            >
              <div className="absolute inset-0 bg-slate-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

              <div className="relative z-10">
                <span className="text-terracotta text-[9px] font-bold tracking-widest uppercase mb-6 block">
                  {region.type}
                </span>

                <h3 className="text-petroleum text-3xl font-black uppercase tracking-tighter mb-2 transition-transform duration-500 group-hover:-translate-y-1">
                  {region.name}
                </h3>
                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mb-10">
                  {region.location}
                </p>

                <div className="space-y-6">
                  <div>
                    <p className="text-petroleum text-[10px] font-black uppercase mb-2 tracking-tight">
                      {region.focusLabel}
                    </p>
                    <p className="text-slate-700 text-sm font-semibold leading-snug">{region.focus}</p>
                  </div>

                  <p className="text-slate-600 text-xs leading-relaxed transition-colors duration-500 group-hover:text-petroleum">
                    {region.description}
                  </p>
                </div>

                <div className="mt-12 h-[2px] bg-slate-100 w-full relative overflow-hidden">
                  <div className="absolute inset-0 bg-terracotta -translate-x-full group-hover:translate-x-0 transition-transform duration-700 ease-in-out" />
                </div>
              </div>
              </m.div>
            ))}
          </div>
        </div>
      </section>
    </LazyMotion>
  );
}
